import { inject, Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';

import { LanguageSelectors, NotificationActions, UniversalCreateActions } from '@ninety/mobile/platform/state';

import { DiscussionApiService } from '../../services/discussion-api.service';
import { CreateDiscussionComponentActions, CreateDiscussionEffectsActions } from '../actions';

@Injectable()
export class CreateDiscussionEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly api = inject(DiscussionApiService);
  private readonly modalCtrl = inject(ModalController);

  showLoadingOnCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateDiscussionComponentActions.createDiscussionClicked),
      map(() => UniversalCreateActions.isCreating({ creating: true }))
    )
  );

  updateCreationText$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateDiscussionComponentActions.createDiscussionClicked),
      concatLatestFrom(() => this.store.select(LanguageSelectors.discussionLanguage)),
      map(([, lang]) =>
        UniversalCreateActions.creationMessage({ creationMessage: `Creating ${lang.item ?? 'Discussion'}...` })
      )
    )
  );

  createDiscussion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateDiscussionComponentActions.createDiscussionClicked),
      filter(({ request }) => !!request),
      switchMap(({ request }) =>
        this.api.createDiscussion(request).pipe(
          map(discussions => CreateDiscussionEffectsActions.createDiscussionSucceeded({ discussion: discussions[0] })),
          catchError(e => {
            console.error(e);
            return of(CreateDiscussionEffectsActions.createDiscussionFailed({ error: e }));
          })
        )
      )
    )
  );

  showToastOnSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateDiscussionEffectsActions.createDiscussionSucceeded),
      concatLatestFrom(() => this.store.select(LanguageSelectors.discussionLanguage)),
      map(([, lang]) =>
        NotificationActions.showToast({
          options: {
            message: `${lang.item} created`,
          },
        })
      )
    )
  );

  closeModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CreateDiscussionEffectsActions.createDiscussionSucceeded),
        tap(() => this.modalCtrl.dismiss())
      ),
    { dispatch: false }
  );

  handleCreateError = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateDiscussionEffectsActions.createDiscussionFailed),
      concatLatestFrom(() => this.store.select(LanguageSelectors.discussionLanguage)),
      map(([, lang]) =>
        NotificationActions.showToast({
          options: {
            type: 'negative',
            message: `Failed to create ${lang.item}. Please try again.`,
          },
        })
      )
    )
  );

  stopLoading = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CreateDiscussionEffectsActions.createDiscussionSucceeded,
        CreateDiscussionEffectsActions.createDiscussionFailed
      ),
      map(() => UniversalCreateActions.isCreating({ creating: false }))
    )
  );
}
